/**
 * ReseauProfilPage — Mon profil & CV de carrière (Lot R2).
 * Édition du parcours (expériences, formations) + partage du profil public.
 * Chaque entrée est étiquetée publique/privée. Export PDF = premium.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Briefcase, GraduationCap, Plus, Trash2, Eye, EyeOff, ExternalLink,
    Copy, FileDown, Loader2, CheckCircle2, Sparkles,
} from 'lucide-react';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

const inputCls = 'w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2';
const inputStyle = {
    background: 'var(--reseau-surface)', color: 'var(--reseau-ink)',
    borderColor: 'var(--reseau-border-strong)',
};

function SectionCard({ title, icon: Icon, children, action }) {
    return (
        <section className="reseau-card p-5">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--reseau-ink-muted)' }}>
                    <Icon className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                    {title}
                </h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function VisibilityBadge({ isPublic }) {
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={isPublic
                  ? { background: 'var(--reseau-violet-soft)', color: 'var(--reseau-violet)' }
                  : { background: 'var(--reseau-bg-muted)', color: 'var(--reseau-ink-muted)' }}>
            {isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {isPublic ? 'Public' : 'Privé'}
        </span>
    );
}

function ExperienceForm({ onSubmit, onCancel, saving }) {
    const [form, setForm] = useState({
        title: '', structure: '', start_year: new Date().getFullYear(),
        end_year: '', zones: '', specializations: '', is_public: true,
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = () => onSubmit({
        title: form.title, structure: form.structure,
        start_year: Number(form.start_year),
        end_year: form.end_year ? Number(form.end_year) : null,
        zones: form.zones.split(',').map((z) => z.trim()).filter(Boolean),
        specializations: form.specializations.split(',').map((s) => s.trim()).filter(Boolean),
        is_public: form.is_public,
    });
    return (
        <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--reseau-bg-muted)' }}
             data-testid="experience-form">
            <input className={inputCls} style={inputStyle} placeholder="Poste (ex. Conseiller immobilier)"
                   value={form.title} onChange={(e) => set('title', e.target.value)}
                   data-testid="exp-title-input" />
            <input className={inputCls} style={inputStyle} placeholder="Structure (ex. Agence du Port)"
                   value={form.structure} onChange={(e) => set('structure', e.target.value)}
                   data-testid="exp-structure-input" />
            <div className="flex gap-3">
                <input type="number" className={inputCls} style={inputStyle} placeholder="Année début"
                       value={form.start_year} onChange={(e) => set('start_year', e.target.value)}
                       data-testid="exp-start-input" />
                <input type="number" className={inputCls} style={inputStyle} placeholder="Année fin (vide = en cours)"
                       value={form.end_year} onChange={(e) => set('end_year', e.target.value)}
                       data-testid="exp-end-input" />
            </div>
            <input className={inputCls} style={inputStyle} placeholder="Zones (séparées par des virgules)"
                   value={form.zones} onChange={(e) => set('zones', e.target.value)} />
            <input className={inputCls} style={inputStyle} placeholder="Spécialisations (séparées par des virgules)"
                   value={form.specializations} onChange={(e) => set('specializations', e.target.value)} />
            <label className="flex items-center gap-2 text-sm cursor-pointer"
                   style={{ color: 'var(--reseau-ink)' }}>
                <input type="checkbox" checked={form.is_public}
                       onChange={(e) => set('is_public', e.target.checked)}
                       data-testid="exp-public-checkbox" />
                Visible sur mon profil public
            </label>
            <div className="flex gap-2">
                <button onClick={submit} disabled={saving || form.title.length < 2}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                        style={{ background: 'var(--reseau-violet)' }}
                        data-testid="exp-save-btn">
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-medium"
                        style={{ color: 'var(--reseau-ink-muted)' }}>
                    Annuler
                </button>
            </div>
        </div>
    );
}

function FormationForm({ onSubmit, onCancel, saving }) {
    const [form, setForm] = useState({ label: '', type: 'formation', year: '', issuer: '', is_public: true });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = () => onSubmit({
        label: form.label, type: form.type,
        year: form.year ? Number(form.year) : null,
        issuer: form.issuer, is_public: form.is_public,
    });
    return (
        <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--reseau-bg-muted)' }}
             data-testid="formation-form">
            <input className={inputCls} style={inputStyle} placeholder="Intitulé (ex. BTS Professions Immobilières)"
                   value={form.label} onChange={(e) => set('label', e.target.value)}
                   data-testid="formation-label-input" />
            <div className="flex gap-3">
                <select className={inputCls} style={inputStyle} value={form.type}
                        onChange={(e) => set('type', e.target.value)}
                        data-testid="formation-type-select">
                    <option value="formation">Formation</option>
                    <option value="diplome">Diplôme</option>
                    <option value="certification">Certification</option>
                </select>
                <input type="number" className={inputCls} style={inputStyle} placeholder="Année"
                       value={form.year} onChange={(e) => set('year', e.target.value)} />
            </div>
            <input className={inputCls} style={inputStyle} placeholder="Organisme (facultatif)"
                   value={form.issuer} onChange={(e) => set('issuer', e.target.value)} />
            <label className="flex items-center gap-2 text-sm cursor-pointer"
                   style={{ color: 'var(--reseau-ink)' }}>
                <input type="checkbox" checked={form.is_public}
                       onChange={(e) => set('is_public', e.target.checked)} />
                Visible sur mon profil public
            </label>
            <div className="flex gap-2">
                <button onClick={submit} disabled={saving || form.label.length < 2}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                        style={{ background: 'var(--reseau-violet)' }}
                        data-testid="formation-save-btn">
                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                <button onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-medium"
                        style={{ color: 'var(--reseau-ink-muted)' }}>
                    Annuler
                </button>
            </div>
        </div>
    );
}

export default function ReseauProfilPage() {
    const [career, setCareer] = useState(null);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showExpForm, setShowExpForm] = useState(false);
    const [showFormationForm, setShowFormationForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const [exportMsg, setExportMsg] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const [me, c] = await Promise.all([api.get('/auth/me'), api.get('/reseau/career/me')]);
            setUser(me.data);
            setCareer(c.data);
        } catch (e) {
            if (e?.response?.status === 401) navigate('/login?next=/reseau/profil');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const addExperience = async (payload) => {
        setSaving(true);
        try {
            await api.post('/reseau/career/experiences', payload);
            setShowExpForm(false);
            await load();
        } finally { setSaving(false); }
    };

    const deleteExperience = async (id) => {
        await api.delete(`/reseau/career/experiences/${id}`);
        await load();
    };

    const addFormation = async (payload) => {
        setSaving(true);
        try {
            await api.post('/reseau/career/formations', payload);
            setShowFormationForm(false);
            await load();
        } finally { setSaving(false); }
    };

    const deleteFormation = async (id) => {
        await api.delete(`/reseau/career/formations/${id}`);
        await load();
    };

    const copyLink = async () => {
        const url = `${window.location.origin}${career.public_url}`;
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            window.prompt('Copiez le lien de votre profil :', url);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const exportPdf = async () => {
        setExportMsg(null);
        try {
            const res = await api.get('/reseau/career/export', { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url; a.download = 'cv-homly.pdf'; a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            if (e?.response?.status === 403) {
                setExportMsg('premium');
            } else {
                setExportMsg('error');
            }
        }
    };

    return (
        <ReseauLayout user={user} headerTitle="Mon profil & CV">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="reseau-profil-page">
                {loading && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                )}
                {!loading && career && (
                    <>
                        <header className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <h1 className="text-xl font-bold" style={{ color: 'var(--reseau-ink)' }}>
                                    {career.identity.first_name} {career.identity.last_name}
                                </h1>
                                <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Votre parcours vous suit — vos mandats restent à l'agence.
                                </p>
                            </div>
                            <Link to={career.public_url} target="_blank"
                                  className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                                  style={{ color: 'var(--reseau-violet)' }}
                                  data-testid="profil-view-public-link">
                                <Eye className="w-4 h-4" />
                                Voir mon profil public
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                        </header>

                        <SectionCard
                            title="Parcours professionnel" icon={Briefcase}
                            action={
                                <button onClick={() => setShowExpForm(true)}
                                        className="inline-flex items-center gap-1 text-sm font-semibold"
                                        style={{ color: 'var(--reseau-violet)' }}
                                        data-testid="profil-add-experience-btn">
                                    <Plus className="w-4 h-4" /> Ajouter
                                </button>
                            }>
                            {showExpForm && (
                                <div className="mb-4">
                                    <ExperienceForm onSubmit={addExperience}
                                                    onCancel={() => setShowExpForm(false)}
                                                    saving={saving} />
                                </div>
                            )}
                            {career.experiences.length === 0 && !showExpForm ? (
                                <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Ajoutez votre première expérience — c'est la colonne vertébrale de votre CV vivant.
                                </p>
                            ) : (
                                <ul className="space-y-3" data-testid="experiences-list">
                                    {[...career.experiences]
                                        .sort((a, b) => (b.start_year || 0) - (a.start_year || 0))
                                        .map((e) => (
                                            <li key={e.id} className="flex items-start justify-between gap-3 p-3 rounded-xl"
                                                style={{ background: 'var(--reseau-bg-muted)' }}
                                                data-testid={`experience-item-${e.id}`}>
                                                <div>
                                                    <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                                        {e.title}{e.structure ? ` — ${e.structure}` : ''}
                                                    </p>
                                                    <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                        {e.start_year}–{e.end_year || "aujourd'hui"}
                                                        {(e.zones || []).length > 0 && ` · ${e.zones.join(', ')}`}
                                                    </p>
                                                    <div className="mt-1"><VisibilityBadge isPublic={e.is_public !== false} /></div>
                                                </div>
                                                <button onClick={() => deleteExperience(e.id)}
                                                        aria-label="Supprimer"
                                                        data-testid={`experience-delete-${e.id}`}
                                                        className="p-1.5 rounded-lg hover:opacity-70"
                                                        style={{ color: 'var(--reseau-danger)' }}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </li>
                                        ))}
                                </ul>
                            )}
                        </SectionCard>

                        <SectionCard
                            title="Formations & certifications" icon={GraduationCap}
                            action={
                                <button onClick={() => setShowFormationForm(true)}
                                        className="inline-flex items-center gap-1 text-sm font-semibold"
                                        style={{ color: 'var(--reseau-violet)' }}
                                        data-testid="profil-add-formation-btn">
                                    <Plus className="w-4 h-4" /> Ajouter
                                </button>
                            }>
                            {showFormationForm && (
                                <div className="mb-4">
                                    <FormationForm onSubmit={addFormation}
                                                   onCancel={() => setShowFormationForm(false)}
                                                   saving={saving} />
                                </div>
                            )}
                            {career.formations.length === 0 && !showFormationForm ? (
                                <p className="text-sm" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Diplômes et certifications renforcent la confiance des vendeurs.
                                </p>
                            ) : (
                                <ul className="space-y-3" data-testid="formations-list">
                                    {[...career.formations]
                                        .sort((a, b) => (b.year || 0) - (a.year || 0))
                                        .map((f) => (
                                            <li key={f.id} className="flex items-start justify-between gap-3 p-3 rounded-xl"
                                                style={{ background: 'var(--reseau-bg-muted)' }}
                                                data-testid={`formation-item-${f.id}`}>
                                                <div>
                                                    <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                                        {f.label}{f.issuer ? ` — ${f.issuer}` : ''}
                                                    </p>
                                                    <p className="text-xs capitalize" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                        {f.type}{f.year ? ` · ${f.year}` : ''}
                                                    </p>
                                                    <div className="mt-1"><VisibilityBadge isPublic={f.is_public !== false} /></div>
                                                </div>
                                                <button onClick={() => deleteFormation(f.id)}
                                                        aria-label="Supprimer"
                                                        data-testid={`formation-delete-${f.id}`}
                                                        className="p-1.5 rounded-lg hover:opacity-70"
                                                        style={{ color: 'var(--reseau-danger)' }}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </li>
                                        ))}
                                </ul>
                            )}
                        </SectionCard>

                        <SectionCard title="Partager mon profil" icon={Sparkles}>
                            <div className="flex flex-wrap gap-3">
                                <button onClick={copyLink}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
                                        style={{ background: 'var(--reseau-violet)' }}
                                        data-testid="profil-copy-link-btn">
                                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {copied ? 'Lien copié !' : 'Copier le lien public'}
                                </button>
                                <button onClick={exportPdf}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border"
                                        style={{ color: 'var(--reseau-violet)', borderColor: 'var(--reseau-border-violet)' }}
                                        data-testid="profil-export-pdf-btn">
                                    <FileDown className="w-4 h-4" />
                                    Exporter mon CV en PDF
                                </button>
                            </div>
                            {exportMsg === 'premium' && (
                                <div className="mt-4 p-4 rounded-xl" data-testid="export-premium-notice"
                                     style={{ background: 'var(--reseau-violet-soft)' }}>
                                    <p className="text-sm font-semibold" style={{ color: 'var(--reseau-violet)' }}>
                                        L'export PDF fait partie de Homly Réseau Premium — 19 € HT/mois.
                                    </p>
                                    <p className="text-xs mt-1" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        Un CV professionnel à jour, utilisable en rendez-vous vendeur ou en candidature.
                                    </p>
                                </div>
                            )}
                            {exportMsg === 'error' && (
                                <p className="text-sm mt-3" style={{ color: 'var(--reseau-danger)' }}>
                                    L'export a échoué. Réessayez dans un instant.
                                </p>
                            )}
                        </SectionCard>
                    </>
                )}
            </div>
        </ReseauLayout>
    );
}
